import type { SectionKey } from '../prompts';
import type { Locale } from '../i18n';

export interface ProjectInput {
  name?: string;
  idea: string;
  industry?: string;
  location?: string;
  teamSize?: string;
  stage?: string;
  goals?: string;
  locale?: Locale;
}

export interface SectionReply {
  id: string;
  blockId: string;
  originalText: string;
  prompt: string;
  response: string;
  createdAt: string;
}

export interface GeneratedSection {
  key: SectionKey;
  content: string;
  status: 'idle' | 'loading' | 'done' | 'error';
  error?: string;
  generatedAt?: string;
  replies?: SectionReply[];
}

// Local-shape project (mirrors localStorage) — used by the section-based
// project workspace UI. Persisted to D1 as a JSON blob in projects.blob_json.
export interface Project {
  id: string;
  name: string;
  input: ProjectInput;
  sections: Record<SectionKey, GeneratedSection>;
  createdAt: string;
  updatedAt: string;
}

// Wire-shape project coming from /api/projects. D1 stores the section-based
// `Project` as JSON in blob_json; workingDoc and extract are surfaced at the
// top level so the interview-flow code can read them without parsing JSON.
export interface ProjectWire {
  id: string;
  userId: string;
  name: string;
  idea: string;
  industry: string | null;
  location: string | null;
  teamSize: string | null;
  stage: string | null;
  goals: string | null;
  locale: 'en' | 'de';
  workingDoc: string;
  extract: Record<string, unknown>;
  blobJson: string | null;
  linkedOrganizationIds: string[];
  createdAt: string;
  updatedAt: string;
}

export function wireToProject(wire: ProjectWire): Project {
  if (wire.blobJson) {
    try {
      const parsed = JSON.parse(wire.blobJson) as Project;
      return {
        id: wire.id,
        name: wire.name,
        input: {
          ...parsed.input,
          name: wire.name,
          idea: wire.idea || parsed.input?.idea || '',
          industry: wire.industry || parsed.input?.industry,
          location: wire.location || parsed.input?.location,
          teamSize: wire.teamSize || parsed.input?.teamSize,
          stage: wire.stage || parsed.input?.stage,
          goals: wire.goals || parsed.input?.goals,
          locale: wire.locale,
        },
        sections: parsed.sections || ({} as Project['sections']),
        createdAt: wire.createdAt,
        updatedAt: wire.updatedAt,
      };
    } catch {
      // fall through to empty sections
    }
  }
  return {
    id: wire.id,
    name: wire.name,
    input: {
      name: wire.name,
      idea: wire.idea,
      industry: wire.industry || undefined,
      location: wire.location || undefined,
      teamSize: wire.teamSize || undefined,
      stage: wire.stage || undefined,
      goals: wire.goals || undefined,
      locale: wire.locale,
    },
    sections: {} as Project['sections'],
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,
  };
}

export function projectToBlob(p: Project): string {
  return JSON.stringify({
    input: p.input,
    sections: p.sections,
  });
}

export interface Organization {
  id: string;
  userId: string;
  name: string;
  idea: string;
  industry: string | null;
  location: string | null;
  teamSize: string | null;
  stage: string | null;
  goals: string | null;
  locale: 'en' | 'de';
  workingDoc: string;
  extract: Record<string, unknown>;
  interviewSessionId: string | null;
  linkedProjectIds: string[];
  documents: Record<string, GeneratedOrgDocument>;
  documentsStatus: Record<string, OrgDocumentStatus>;
  documentsErrors: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export type OrgDocumentStatus = 'pending' | 'generating' | 'done' | 'error';

export interface GeneratedOrgDocument {
  content: string;
  generatedAt: string;
}

export interface AppSettings {
  darkMode: boolean;
}

export const DEFAULT_SETTINGS: AppSettings = {
  darkMode: false,
};
